import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CustomerFollowUpQueue,
  FollowUpQueueScope,
  Task,
  TaskStatus,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { customerKeys } from '../customers/use-customers.js';
import type { DateInput } from '../format.js';
import { shopDayStart } from './follow-up-state.js';
import { getFollowUpQueue, setCustomerTaskStatus } from './follow-ups-api.js';

/**
 * The follow-ups queue, as data — what the Follow-ups page and the header bell read.
 *
 * THE KEYS LIVE UNDER `customers`, on purpose: every write to a customer's tasks (on their page,
 * or ticking a row here) invalidates `customerKeys.all`, and a queue keyed anywhere else would go
 * on listing a task somebody has just finished.
 */
export const followUpKeys = {
  all: ['customers', 'follow-ups'] as const,
  queue: (scope: FollowUpQueueScope) => ['customers', 'follow-ups', scope] as const,
  /**
   * The header panel's windowed slice — distinct from `queue` so the panel's capped query and the
   * page's uncapped one never hand each other the wrong list.
   */
  window: (scope: FollowUpQueueScope, dueBefore: string) =>
    ['customers', 'follow-ups', scope, dueBefore] as const,
};

/** Everything still open, soonest first. What the full page reads. */
export function useFollowUpQueue(
  scope: FollowUpQueueScope = 'mine',
): UseQueryResult<CustomerFollowUpQueue> {
  const client = useApiClient();
  return useQuery({
    queryKey: followUpKeys.queue(scope),
    queryFn: () => getFollowUpQueue(client, scope),
  });
}

/** Everything owed up to the end of tomorrow, in the shop's day — the end of the header panel. */
export function followUpWindowEnd(now: DateInput = Date.now()): string {
  return new Date(shopDayStart(now).getTime() + 2 * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * What the header bell reads: overdue, today and tomorrow, and nothing further out.
 *
 * The window is computed from `shopDayStart` rather than from the browser's midnight, because
 * the SERVER counts `overdue` and `dueToday` from the shop's day boundary and a panel drawing
 * midnight somewhere else would show a row it had just told you was not due.
 *
 * The boundary doubles as part of the query key: it is one stable string for the whole day, so it
 * does not re-key on every render, and it changes by itself when the shop's day rolls over —
 * which is how a tab left open overnight notices.
 *
 * There is no realtime channel in this app, so this refetches when you come back to the tab and
 * quietly every five minutes. Your own ticks are instant, via the invalidation below.
 */
export function useFollowUpWindow(
  scope: FollowUpQueueScope = 'mine',
): UseQueryResult<CustomerFollowUpQueue> {
  const client = useApiClient();
  const dueBefore = followUpWindowEnd();
  return useQuery({
    queryKey: followUpKeys.window(scope, dueBefore),
    queryFn: () => getFollowUpQueue(client, scope, dueBefore),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 300_000,
  });
}

/**
 * Setting one of a customer's tasks' status — the tick on a follow-up row.
 *
 * Invalidates the whole customer book, which covers the customer's own task list, their cached
 * next-follow-up date, and every copy of the queue.
 */
export function useSetCustomerTaskStatus(customerId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Task, unknown, { taskId: string; status: TaskStatus }>({
    mutationFn: ({ taskId, status }) => setCustomerTaskStatus(client, customerId, taskId, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}
